import { Type } from 'class-transformer';
import { BulkCreateMarketItemDto } from './create-market.dto';
import {
	IsArray,
	IsDateString,
	IsEnum,
	IsIn,
	IsInt,
	IsNotEmpty,
	IsNumber,
	IsOptional,
	IsString,
	IsUUID,
	Max,
	Min,
	ValidateNested,
} from 'class-validator';
import { GameCategory } from '../../enums/optic-odds.enum';

export enum MarketTimeField {
	Opening = 'opening_time',
	Closing = 'closing_date',
}

export class MarketIdDto {
	@IsUUID()
	marketId: string;
}

export class SettleMarketDto extends MarketIdDto {
	@IsNumber()
	@IsIn([0, 100])
	settledValue: number;
}

export class UnsettleMarketDto extends MarketIdDto {}

export class CloseMarketDto extends MarketIdDto {}

export class VoidMarketDto extends MarketIdDto {}

export class PushNextMarketToLiveDto extends MarketIdDto {
	@IsInt()
	@Min(0)
	marketOpenDelay: number;
}

export class BulkSettleMarketsDto {
	@IsUUID()
	eventId: string;

	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => SettleMarketDto)
	markets: SettleMarketDto[];
}

export class BulkUpdateMarketContextItemDto {
	@IsUUID()
	marketId: string;

	@IsOptional()
	@IsString()
	context?: string | null;
}

export class BulkUpdateMarketContextDto {
	@IsUUID()
	eventId: string;

	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => BulkUpdateMarketContextItemDto)
	markets: BulkUpdateMarketContextItemDto[];
}

export class BulkUnsettleMarketsDto {
	@IsUUID()
	eventId: string;

	@IsArray()
	@IsUUID('4', { each: true })
	marketIds: string[];
}

export class BulkDeleteMarketsDto {
	@IsUUID()
	eventId: string;

	@IsArray()
	@IsUUID('4', { each: true })
	marketIds: string[];
}

export class BulkShiftMarketTimeDto {
	@IsUUID()
	eventId: string;

	@IsDateString()
	@IsNotEmpty()
	time: string;

	@IsEnum(MarketTimeField)
	field: MarketTimeField;

	@IsArray()
	@IsUUID('4', { each: true })
	marketIds: string[];
}

export class BulkUpdateOddsDto {
	@IsUUID()
	eventId: string;

	@IsInt()
	@Min(1)
	@Max(99)
	value: number;

	@IsArray()
	@IsUUID('4', { each: true })
	marketIds: string[];

	@IsOptional()
	@IsInt()
	@Min(1)
	@Max(10)
	duration_minutes?: number;
}

export class BulkUpdateMarketCategoryDto {
	@IsUUID()
	eventId: string;

	@IsString()
	@IsNotEmpty()
	categoryId: string;

	@IsArray()
	@IsUUID('4', { each: true })
	marketIds: string[];
}

export class ReorderMarketPriorityDto {
	@IsUUID()
	eventId: string;

	@IsUUID()
	marketId: string;

	@IsOptional()
	@IsInt()
	@Min(1)
	newPriority?: number;

	@IsOptional()
	@IsUUID()
	anchorMarketId?: string;

	@IsOptional()
	@IsIn(['before', 'after'])
	position?: 'before' | 'after';

	@IsOptional()
	@IsArray()
	@IsUUID('4', { each: true })
	orderedMarketIds?: string[];
}

export class GenerateNewMarketsDto {
	@IsString()
	@IsNotEmpty()
	gameId: string;

	@IsUUID()
	matchId: string;

	@IsArray()
	@IsString({ each: true })
	@IsOptional()
	marketGroups?: string[];
}

export class GenerateFuturesMarketsDto {
	@IsString()
	@IsNotEmpty()
	categoryId: string;

	@IsArray()
	@IsString({ each: true })
	futureNames: string[];

	@IsUUID()
	eventId: string;
}

export class GenerateEspnMarketsDto {
	@IsUUID()
	matchId: string;

	@IsUUID()
	eventId: string;

	@IsString()
	@IsNotEmpty()
	espnEventId: string;

	@IsEnum(GameCategory)
	category: GameCategory;
}

export class BulkCreateMarketsDto {
	@IsUUID()
	event_id: string;

	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => BulkCreateMarketItemDto)
	data: BulkCreateMarketItemDto[];

	@IsOptional()
	@IsUUID()
	match_id?: string;

	@IsOptional()
	@IsInt()
	@Min(0)
	market_open_delay?: number;
}
